import { useCallback, useEffect, useRef, useState } from 'react';
import { getApiErrorMessage } from '../api/client';
import { useI18n } from '../i18n';
import { fetchPublicBracket } from '../api/competitionBracket';
import { BRACKET_DISPLAY_POLL_MS } from '../utils/bracketDisplaySync';
import { fromBracketResponse } from '../utils/bracketStorage';
import { setBracketSetSelectedHeightRange } from '../utils/bracketSet';
import { normalizeParticipantList } from '../utils/participantList';

const IDLE_STATE = {
  status: 'idle',
  /** 화면 문구 키 (loading / empty) */
  messageKey: null,
  /** status 가 error 일 때의 오류 (문구는 반환할 때 현재 언어로 만든다) */
  error: null,
  bracketSet: null,
  participants: [],
  results: [],
  /** 이 대회에서 진행 중인 경기들 [{ court, divisionKey, roundIndex, matchIndex }] */
  liveMatches: [],
};

/**
 * 공개 대진표 조회 + 주기적 갱신 (조회 전용).
 * status: idle | loading | empty | error | ready
 */
function usePublicBracket(tournamentId) {
  const { t } = useI18n();
  const [state, setState] = useState(IDLE_STATE);
  const currentIdRef = useRef(tournamentId);
  /** 화면에서 직접 고른 키 구간 (갱신 후에도 유지) */
  const selectedRangeRef = useRef(null);

  const load = useCallback(
    async ({ silent = false } = {}) => {
      if (tournamentId == null || tournamentId === '') return;
      if (!silent) {
        setState({ ...IDLE_STATE, status: 'loading', messageKey: 'tournament.public.loading' });
      }

      try {
        const data = await fetchPublicBracket(tournamentId);
        if (currentIdRef.current !== tournamentId) return;

        if (!data) {
          setState({ ...IDLE_STATE, status: 'empty', messageKey: 'tournament.public.empty' });
          return;
        }

        const participants = normalizeParticipantList(data.participants);
        let bracketSet = fromBracketResponse(data.bracket, participants);
        if (selectedRangeRef.current) {
          bracketSet = setBracketSetSelectedHeightRange(bracketSet, selectedRangeRef.current);
        }
        setState({
          status: 'ready',
          messageKey: null,
          error: null,
          bracketSet,
          participants,
          results: data.results ?? [],
          liveMatches: data.liveMatches ?? [],
        });
      } catch (error) {
        // 주기적 갱신 중 오류는 화면을 바꾸지 않는다
        if (silent || currentIdRef.current !== tournamentId) return;
        setState({
          ...IDLE_STATE,
          status: 'error',
          error,
        });
      }
    },
    [tournamentId]
  );

  useEffect(() => {
    currentIdRef.current = tournamentId;
    selectedRangeRef.current = null;
    if (tournamentId == null || tournamentId === '') {
      setState(IDLE_STATE);
      return undefined;
    }
    load();
    const timer = setInterval(() => load({ silent: true }), BRACKET_DISPLAY_POLL_MS);
    return () => clearInterval(timer);
  }, [tournamentId, load]);

  const selectHeightRange = useCallback((rangeId) => {
    selectedRangeRef.current = rangeId;
    setState((prev) => ({
      ...prev,
      bracketSet: setBracketSetSelectedHeightRange(prev.bracketSet, rangeId),
    }));
  }, []);

  const { messageKey, error, ...rest } = state;
  let message = '';
  if (rest.status === 'error') message = getApiErrorMessage(error, t('tournament.errors.loadBracket'));
  else if (messageKey) message = t(messageKey);

  return { ...rest, message, reload: load, selectHeightRange };
}

export default usePublicBracket;
